import { useMemo } from 'react';
import { useCheckPaymentExpiration, useGetPaymentMethods } from '~/domain/Payments';
import { useSelectedOffice } from '~/hooks/useSelectedOffice';

export interface IPaymentEligibility {
  /** Office can pay — pre-approved, or it has a selected, non-expired payment method. */
  isEligible: boolean;
  /**
   * The payment-methods query is still resolving. Never true for pre-approved offices
   * (their query is skipped), so consumers can safely default to the valid/unlocked
   * state while this is true without flashing a locked state before data arrives.
   */
  isLoading: boolean;
}

/**
 * Resolves whether the effective office can pay, plus whether that answer is still loading.
 * Single source of truth for gating value-added features (invite / auto-confirm) that
 * require payment.
 *
 * Gating is driven by the **offer owner** (`officeId`) and, unlike the payment-reminder
 * banner, must NOT exempt parent offices: a parent office posting for itself without a
 * payment method should still be gated. We therefore evaluate the effective office's own
 * payment state directly instead of reusing `useCheckPaymentExpiration`'s `isInvalidPayment`,
 * which intentionally exempts parent offices for the notification banner (GOTU-2479) and is
 * derived from the sidebar-selected office rather than the offer owner.
 */
export const usePaymentEligibility = (officeId?: string): IPaymentEligibility => {
  const office = useSelectedOffice(officeId);
  const isPreApproved = Boolean(office?.is_pre_approved);

  // Always resolve payments for the offer owner; only skip when pre-approved (payment moot).
  const { paymentMethods, isLoading } = useGetPaymentMethods(officeId, {
    enabled: !isPreApproved,
  });

  // Reuse the shared expiry helper so card-expiry rules live in exactly one place.
  const { checkPaymentExpiration } = useCheckPaymentExpiration(officeId);

  const isEligible = useMemo(() => {
    if (isPreApproved) {
      return true;
    }

    const validPayments = paymentMethods.filter(
      (payment) => payment.status !== 'pending',
    );
    if (!validPayments.length) {
      return false;
    }

    // A non-pending method exists, but validity hangs on the *selected* one. If none is
    // selected, `checkPaymentExpiration(undefined)` reports expired → invalid.
    const selectedPayment = paymentMethods.find((payment) => payment.is_selected);
    return !checkPaymentExpiration(selectedPayment).isExpired;
  }, [isPreApproved, paymentMethods, checkPaymentExpiration]);

  // Pre-approved offices never fetch, so they are never "loading".
  return { isEligible, isLoading: !isPreApproved && Boolean(isLoading) };
};
